import React, { createContext, useEffect, useMemo, useState } from "react";

export const CartContext = createContext();

const readStorage = (key, fallback) => {
  if (typeof window === "undefined") return fallback;
  try {
    const value = JSON.parse(window.localStorage.getItem(key));
    return Array.isArray(value) ? value : fallback;
  } catch {
    return fallback;
  }
};

const validCartItem = (item) =>
  item &&
  Number.isInteger(Number(item.id)) &&
  Number.isFinite(Number(item.quantity)) &&
  Number(item.quantity) > 0;

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() =>
    readStorage("cartItems", []).filter(validCartItem).map((item) => ({
      ...item,
      id: Number(item.id),
      quantity: Math.max(1, Math.floor(Number(item.quantity))),
      size: item.size || "Standard",
      color: item.color || "Default",
    }))
  );
  const [wishlist, setWishlist] = useState(() =>
    readStorage("wishlist", [])
      .map(Number)
      .filter(Number.isInteger)
  );
  const [isCartOpen, setIsCartOpen] = useState(false);

  useEffect(() => {
    window.localStorage.setItem("cartItems", JSON.stringify(cartItems));
  }, [cartItems]);

  useEffect(() => {
    window.localStorage.setItem("wishlist", JSON.stringify(wishlist));
  }, [wishlist]);

  const addToCart = (productId, quantity = 1, size = "Standard", color = "Default") => {
    const id = Number(productId);
    const amount = Math.max(1, Math.floor(Number(quantity) || 1));
    setCartItems((current) => {
      const existing = current.find(
        (item) => item.id === id && item.size === size && item.color === color
      );
      if (!existing) return [...current, { id, size, color, quantity: amount }];
      return current.map((item) =>
        item.id === id && item.size === size && item.color === color
          ? { ...item, quantity: item.quantity + amount }
          : item
      );
    });
  };

  const removeFromCart = (productId, size, color) => {
    setCartItems((current) =>
      current.filter(
        (item) => !(item.id === Number(productId) && item.size === size && item.color === color)
      )
    );
  };

  const updateQuantity = (productId, size, color, quantity) => {
    const nextQuantity = Math.max(1, Math.floor(Number(quantity) || 1));
    setCartItems((current) =>
      current.map((item) =>
        item.id === Number(productId) && item.size === size && item.color === color
          ? { ...item, quantity: nextQuantity }
          : item
      )
    );
  };

  const clearCart = () => setCartItems([]);

  const toggleWishlist = (productId) => {
    const id = Number(productId);
    setWishlist((current) =>
      current.includes(id) ? current.filter((itemId) => itemId !== id) : [...current, id]
    );
  };

  const cartCount = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.quantity, 0),
    [cartItems]
  );

  const value = {
    cartItems,
    wishlist,
    cartCount,
    isCartOpen,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    toggleWishlist,
    openCart: () => setIsCartOpen(true),
    closeCart: () => setIsCartOpen(false),
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
